/**
 * Parse SQL into statement segments while tracking original-source offsets.
 * Used by the "current statement" cursor feature: highlights the statement
 * under the cursor and anchors the floating Execute|Peek bar to it.
 *
 * Engine-aware (best-effort heuristics until the tree-sitter SQL grammar
 * lands) — respects the quoting / comment / batch-separator conventions of
 * each supported engine:
 *
 *   - `pgsql` — dollar-quoted strings (`$tag$ ... $tag$`) and nested block
 *     comments are common enough in stored-procedure bodies that splitting
 *     without them would produce garbage segments.
 *   - `mssql` — a line containing only `GO` (any case) is a T-SQL batch
 *     separator; SSMS / sqlcmd treat it as a statement boundary.
 *   - `mysql` / `sqlite` — backtick-quoted identifiers.
 *   - all engines — single-quoted strings, double-quoted identifiers,
 *     bracket-quoted identifiers (MSSQL), line + block comments, blank-line
 *     separators (2+ consecutive newlines outside strings/comments).
 */

import type { DatabaseEngineId } from '@/core/engines';

export type StatementSegment = {
  /** Raw span start offset in the source SQL (inclusive). */
  rawStart: number;
  /** Raw span end offset in the source SQL (exclusive). Includes trailing `;` if present. */
  rawEnd: number;
  /** Offset where the first non-whitespace char of the statement begins. */
  trimStart: number;
  /** Offset immediately after the last non-whitespace char (excludes trailing `;` / `GO`). */
  trimEnd: number;
  /** Trimmed statement text (no leading/trailing whitespace, no trailing `;` / `GO`). */
  text: string;
};

/**
 * Split SQL into offset-tracked segments. Segments preserve their
 * original-source boundaries so callers can map a cursor offset back to a
 * source range (for highlighting + pixel-positioning).
 *
 * Statements are split on any of:
 *   - `;` outside strings / comments
 *   - A blank line (2+ consecutive newlines with only whitespace between)
 *     outside strings / comments — users frequently write multi-statement
 *     scripts without trailing semicolons.
 *   - A `GO` batch separator on its own line, for MSSQL only.
 */
export function parseStatementSegments(
  sql: string,
  engineId?: DatabaseEngineId
): StatementSegment[] {
  const segments: StatementSegment[] = [];
  if (!sql) return segments;

  const isPg = engineId === 'pgsql';
  const isMssql = engineId === 'mssql';

  let segStart = 0;
  let i = 0;

  const pushSegment = (start: number, end: number) => {
    // Compute the trimmed inner span.
    let ts = start;
    while (ts < end && isWhitespace(sql[ts])) ts++;
    let te = end;
    while (te > ts && isWhitespace(sql[te - 1])) te--;
    // Drop trailing `;` from trimEnd so the highlighted range doesn't cover it.
    if (te > ts && sql[te - 1] === ';') {
      te--;
      while (te > ts && isWhitespace(sql[te - 1])) te--;
    }
    // Drop a trailing MSSQL `GO` batch separator if present.
    if (isMssql && te - ts >= 2) {
      const last = sql.slice(Math.max(ts, te - 4), te);
      const match = last.match(/(^|[\s])GO\s*$/i);
      if (match) {
        te -= match[0].length - (match[1] ? match[1].length : 0);
        while (te > ts && isWhitespace(sql[te - 1])) te--;
      }
    }
    const text = sql.slice(ts, te).trim();
    if (!text) return;
    segments.push({ rawStart: start, rawEnd: end, trimStart: ts, trimEnd: te, text });
  };

  while (i < sql.length) {
    const ch = sql[i];
    const next = sql[i + 1];

    // Line comment
    if (ch === '-' && next === '-') {
      i += 2;
      while (i < sql.length && sql[i] !== '\n') i++;
      continue;
    }

    // Block comment — nested for pgsql only
    if (ch === '/' && next === '*') {
      if (isPg) {
        let depth = 1;
        i += 2;
        while (i < sql.length && depth > 0) {
          if (sql[i] === '/' && sql[i + 1] === '*') { depth++; i += 2; continue; }
          if (sql[i] === '*' && sql[i + 1] === '/') { depth--; i += 2; continue; }
          i++;
        }
      } else {
        i += 2;
        while (i < sql.length && !(sql[i] === '*' && sql[i + 1] === '/')) i++;
        if (i < sql.length) i += 2;
      }
      continue;
    }

    // Dollar-quoted string (pgsql)
    if (isPg && ch === '$') {
      const delimiter = readDollarDelimiter(sql, i);
      if (delimiter) {
        i += delimiter.length;
        while (i < sql.length && !sql.startsWith(delimiter, i)) i++;
        if (i < sql.length) i += delimiter.length;
        continue;
      }
    }

    // Single-quoted string
    if (ch === "'") {
      i++;
      while (i < sql.length) {
        if (sql[i] === "'" && sql[i + 1] === "'") { i += 2; continue; }
        if (sql[i] === "'") { i++; break; }
        i++;
      }
      continue;
    }

    // Double-quoted identifier (ANSI / mssql / pgsql / oracle)
    if (ch === '"') {
      i++;
      while (i < sql.length) {
        if (sql[i] === '"' && sql[i + 1] === '"') { i += 2; continue; }
        if (sql[i] === '"') { i++; break; }
        i++;
      }
      continue;
    }

    // Bracket identifier (MSSQL)
    if (ch === '[') {
      i++;
      while (i < sql.length && sql[i] !== ']') i++;
      if (i < sql.length) i++;
      continue;
    }

    // Backtick identifier (MySQL / SQLite)
    if (ch === '`') {
      i++;
      while (i < sql.length && sql[i] !== '`') i++;
      if (i < sql.length) i++;
      continue;
    }

    // Statement terminator: semicolon.
    if (ch === ';') {
      pushSegment(segStart, i + 1);
      segStart = i + 1;
      i++;
      continue;
    }

    // Statement terminator: blank line (2+ consecutive newlines outside
    // strings/comments). Handles LF and CRLF.
    if (ch === '\n') {
      const blankEnd = blankLineRunEnd(sql, i);
      if (blankEnd !== null) {
        pushSegment(segStart, i + 1);
        segStart = blankEnd;
        i = blankEnd;
        continue;
      }
      // Single newline — check for MSSQL `GO` batch separator on its own line.
      if (isMssql) {
        const goEnd = matchGoBatchSeparator(sql, i);
        if (goEnd !== null) {
          // Include the leading newline + `GO` + trailing newline (if any)
          // in the split point. The segment up to (and including) the
          // newline before `GO` is pushed; the next segment starts after
          // the GO line.
          pushSegment(segStart, i + 1);
          segStart = goEnd;
          i = goEnd;
          continue;
        }
      }
      i++;
      continue;
    }

    i++;
  }

  // Trailing chunk (no final separator).
  if (segStart < sql.length) {
    pushSegment(segStart, sql.length);
  }

  return segments;
}

function isWhitespace(ch: string): boolean {
  return ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '\f' || ch === '\v';
}

/**
 * If `sql[newlineIdx]` starts a run of 2+ newlines (possibly separated by
 * tabs/spaces/CRs), return the offset immediately after the last whitespace
 * character in that blank-line run. Otherwise return null.
 */
function blankLineRunEnd(sql: string, newlineIdx: number): number | null {
  let j = newlineIdx + 1;
  while (j < sql.length && (sql[j] === ' ' || sql[j] === '\t' || sql[j] === '\r')) j++;
  if (j >= sql.length || sql[j] !== '\n') return null;
  // Swallow the rest of the blank-line run.
  let k = j;
  while (
    k < sql.length &&
    (sql[k] === ' ' || sql[k] === '\t' || sql[k] === '\r' || sql[k] === '\n')
  ) {
    k++;
  }
  return k;
}

/**
 * If `sql[newlineIdx]` is followed by a line containing only `GO` (MSSQL batch
 * separator), return the offset immediately after that line. Otherwise null.
 * The separator must occupy its own line — `GO` anywhere else (e.g., as an
 * identifier) is not a separator.
 */
function matchGoBatchSeparator(sql: string, newlineIdx: number): number | null {
  let j = newlineIdx + 1;
  // Skip leading spaces/tabs on the next line.
  while (j < sql.length && (sql[j] === ' ' || sql[j] === '\t')) j++;
  // Match "GO" (case-insensitive) as the only token on the line.
  if (j + 1 >= sql.length) return null;
  const c0 = sql[j];
  const c1 = sql[j + 1];
  if (!(c0 === 'G' || c0 === 'g') || !(c1 === 'O' || c1 === 'o')) return null;
  // The char after GO must be whitespace / EOF / newline — no trailing chars.
  let k = j + 2;
  while (k < sql.length && (sql[k] === ' ' || sql[k] === '\t')) k++;
  if (k < sql.length && sql[k] !== '\n' && sql[k] !== '\r') return null;
  // Swallow up to (and including) the line terminator after GO.
  if (k < sql.length && sql[k] === '\r') k++;
  if (k < sql.length && sql[k] === '\n') k++;
  return k;
}

/** Read a pgsql dollar-quote delimiter starting at `startIndex`, or null. */
function readDollarDelimiter(sql: string, startIndex: number): string | null {
  if (sql[startIndex] !== '$') return null;
  let i = startIndex + 1;
  while (i < sql.length && /[A-Za-z0-9_]/.test(sql[i])) i++;
  if (i < sql.length && sql[i] === '$') return sql.slice(startIndex, i + 1);
  return null;
}

/**
 * Find the statement segment that contains (or is closest to) the cursor offset.
 *
 * Semicolons / GO / blank lines belong to the statement BEFORE them — a cursor
 * exactly on the separator returns the statement it terminates. A cursor in
 * whitespace/comments between statements returns null.
 */
export function getStatementAtCursor(
  sql: string,
  cursorOffset: number,
  engineId?: DatabaseEngineId
): StatementSegment | null {
  if (!sql) return null;
  return findSegmentAtOffset(parseStatementSegments(sql, engineId), cursorOffset);
}

/**
 * Find the segment containing the cursor offset in already-parsed segments.
 * Split out from `getStatementAtCursor` so callers holding cached segments
 * (see `useStatementSegments`) can look up the cursor's statement without
 * re-parsing the document.
 */
export function findSegmentAtOffset(
  segments: StatementSegment[],
  cursorOffset: number
): StatementSegment | null {
  for (const seg of segments) {
    if (cursorOffset >= seg.trimStart && cursorOffset <= seg.rawEnd) {
      return seg;
    }
  }
  return null;
}

/**
 * The segment a selection covers exactly: `[start, end)` is the selection with
 * its surrounding whitespace trimmed, and it must start at the statement's
 * first character and end at its last one or anywhere up to its terminating
 * `;` / `GO`. A partial or multi-statement selection returns null.
 */
export function findSegmentCoveredBySelection(
  segments: StatementSegment[],
  start: number,
  end: number
): StatementSegment | null {
  for (const seg of segments) {
    if (start === seg.trimStart && end >= seg.trimEnd && end <= seg.rawEnd) {
      return seg;
    }
  }
  return null;
}

/** Convenience: count the number of statement segments. */
export function getStatementCount(sql: string, engineId?: DatabaseEngineId): number {
  return parseStatementSegments(sql, engineId).length;
}

/**
 * Walk backwards from `lineNumber` (Monaco 1-based) and return the nearest
 * line above it that contains non-whitespace text. Returns null if every
 * preceding line is blank (or the target is already line 1). Used by
 * floating widgets that need to know where the previous statement's text
 * ends, so they can decide whether there's vertical room to render above
 * the current statement without overlapping the one above.
 */
export function findPreviousNonBlankLine(
  content: string,
  lineNumber: number
): number | null {
  if (!content || lineNumber <= 1) return null;
  const lines = content.split('\n');
  for (let i = lineNumber - 2; i >= 0; i--) {
    if (lines[i] && lines[i].trim() !== '') return i + 1;
  }
  return null;
}

/**
 * Mirror of `findPreviousNonBlankLine` for the forward direction — returns
 * the nearest line below `lineNumber` (Monaco 1-based) that contains
 * non-whitespace text. Null when the rest of the document is blank.
 */
export function findNextNonBlankLine(
  content: string,
  lineNumber: number
): number | null {
  if (!content) return null;
  const lines = content.split('\n');
  for (let i = lineNumber; i < lines.length; i++) {
    if (lines[i] && lines[i].trim() !== '') return i + 1;
  }
  return null;
}

/**
 * Convert a zero-based character offset in `content` to Monaco's 1-based
 * line/column coordinates.
 */
export function offsetToLineColumn(
  content: string,
  offset: number
): { lineNumber: number; column: number } {
  const clamped = Math.max(0, Math.min(offset, content.length));
  let lineNumber = 1;
  let lastLineStart = 0;
  for (let i = 0; i < clamped; i++) {
    if (content[i] === '\n') {
      lineNumber += 1;
      lastLineStart = i + 1;
    }
  }
  return { lineNumber, column: clamped - lastLineStart + 1 };
}
